import { createReference, type MedplumClient } from "@medplum/core";
import type { Provenance, ProvenanceAgent } from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import { captureExceptionWithoutPhi } from "@/utils/sentry";

import type {
  RecordVersionedTargetProvenanceContext,
  RecordVersionedTargetProvenanceExtra,
} from "../types/record-versioned-target-provenance";

export const RECORD_VERSIONED_TARGET_PROVENANCE_FAILURE = {
  NoProfile: "no-profile",
  WriteFailed: "write-failed",
} as const;

export type RecordVersionedTargetProvenanceFailure =
  (typeof RECORD_VERSIONED_TARGET_PROVENANCE_FAILURE)[keyof typeof RECORD_VERSIONED_TARGET_PROVENANCE_FAILURE];

export type RecordVersionedTargetProvenanceResult =
  | { ok: true; data: Provenance }
  | { ok: false; reason: RecordVersionedTargetProvenanceFailure; details?: string };

function toSentryExtra(
  extra: RecordVersionedTargetProvenanceExtra,
): Record<string, string | undefined> {
  return {
    activityCode: extra.activityCode,
    invoiceId: extra.invoiceId,
    chargeItemId: extra.chargeItemId,
    documentReferenceId: extra.documentReferenceId,
    paymentReconciliationId: extra.paymentReconciliationId,
  };
}

/**
 * Creates a Provenance for a mutation that has already been persisted.
 *
 * Reports rather than throws. Rejecting here would tell the user their save failed when it did not.
 * The caller keeps its own result and the history shows a gap, which Sentry reports.
 *
 * @param medplum The Medplum client.
 * @param buildProvenance Builds the unsaved Provenance once an acting profile is available.
 * @param context Sentry copy and extras for a missing profile or a failed write.
 * @returns The created Provenance, or the reason it could not be written.
 */
export async function recordVersionedTargetProvenance(
  medplum: MedplumClient,
  buildProvenance: (agentReference: ProvenanceAgent["who"]) => Provenance,
  context: RecordVersionedTargetProvenanceContext,
): Promise<RecordVersionedTargetProvenanceResult> {
  const profile = medplum.getProfile();
  if (!profile) {
    Sentry.captureMessage(context.noProfileMessage, {
      level: "warning",
      extra: toSentryExtra(context.extra),
    });
    return { ok: false, reason: RECORD_VERSIONED_TARGET_PROVENANCE_FAILURE.NoProfile };
  }

  try {
    const provenance = await medplum.createResource(buildProvenance(createReference(profile)));
    return { ok: true, data: provenance };
  } catch (error) {
    captureExceptionWithoutPhi(
      "Provenance write failed after the mutation was saved",
      error,
      toSentryExtra(context.extra),
      "warning",
    );
    return {
      ok: false,
      reason: RECORD_VERSIONED_TARGET_PROVENANCE_FAILURE.WriteFailed,
      details: error instanceof Error ? error.message : undefined,
    };
  }
}
